/**
 * IdeaDetailBody
 *
 * The one idea-detail body, in DD-013 order: assets first (primary
 * resource embed + sources), then the optional production tasks slot,
 * then the text body sections. Every chrome renders this — admin page,
 * admin modal, creator drawer, and the client review drawer — so the
 * surfaces cannot drift.
 *
 * Editability is a prop, not a fork: pass ``onSectionSave`` (and
 * optionally ``uploadFile``) to get the section editors, omit it for
 * the read-only client rendering. The admin's editable sources row and
 * tasks checklist arrive as slots; without ``sourcesSlot`` a read-only
 * sources row renders from ``resources``.
 */

import React from "react";
import { PrimaryResourceEmbed } from "@/components/ui/PrimaryResourceEmbed";
import { ResourceEmbedCard } from "@/components/ui/ResourceEmbedCard";
import { IdeaBodySections } from "./IdeaBodySections";
import { IdeaBody, IdeaBodySectionKey, IdeaResource } from "../types";

interface IdeaDetailBodyProps {
  resources: IdeaResource[];
  body: IdeaBody;
  /** Admin's editable sources row (label included). Omit for a read-only row. */
  sourcesSlot?: React.ReactNode;
  /** Admin-only production tasks block; omitted in portal chromes. */
  tasksSlot?: React.ReactNode;
  /** Present → sections are editable. Absent → read-only, empty sections skipped. */
  onSectionSave?: (key: IdeaBodySectionKey, value: string) => void;
  uploadFile?: (file: File) => Promise<string>;
  /** Localized overrides for section labels (portal chromes). */
  sectionLabels?: Partial<Record<IdeaBodySectionKey, string>>;
  /** Localized "Sources" heading for the read-only row. */
  sourcesLabel?: string;
}

export const IdeaDetailBody: React.FC<IdeaDetailBodyProps> = ({
  resources,
  body,
  sourcesSlot,
  tasksSlot,
  onSectionSave,
  uploadFile,
  sectionLabels,
  sourcesLabel = "Sources",
}) => {
  return (
    <div className="space-y-5">
      <PrimaryResourceEmbed resources={resources} />

      {sourcesSlot ??
        (resources.length > 0 && (
          <div>
            <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-[0.1em] mb-2">
              {sourcesLabel}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {resources.map((resource, index) => (
                <ResourceEmbedCard key={index} resource={resource} variant="dense" />
              ))}
            </div>
          </div>
        ))}

      {tasksSlot}

      <IdeaBodySections
        body={body}
        onSectionSave={onSectionSave}
        uploadFile={uploadFile}
        labels={sectionLabels}
      />
    </div>
  );
};
